import { Spinner } from '@optools/components/loading/spinner';
import { TeamSpot } from '@optools/components/team-spot';
import { Badge } from '@optools/components/ui/badge';
import { Button } from '@optools/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@optools/components/ui/command';
import { Label } from '@optools/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@optools/components/ui/popover';
import { useTeamsQuery } from '@optools/store/statsApiSlice';
import { Check, ChevronDownIcon, HelpCircle } from 'lucide-react';
import { useId, useState } from 'react';

export interface TeamPickerProps {
  disabled: boolean;
  loading: boolean;
  message: string;
  onSelectTeam: (teamId: number) => void;
  selectedTeamId: number | null | undefined;
  title: string;
}

export function TeamPicker({
  disabled,
  loading,
  message,
  onSelectTeam,
  selectedTeamId,
  title,
}: TeamPickerProps) {
  const [open, setOpen] = useState(false);
  const id = useId();

  const { data, isLoading: teamsLoading } = useTeamsQuery();
  const selectedTeam = data?.find((team) => team.id === selectedTeamId);

  return (
    <div className="flex flex-col items-start gap-2">
      <div className="mb-1 flex gap-1">
        <Label htmlFor={id}>{title}</Label>
        {disabled && (
          <Popover>
            <PopoverTrigger>
              <HelpCircle className="size-4" />
            </PopoverTrigger>
            <PopoverContent>
              <p className="text-sm">{message}</p>
            </PopoverContent>
          </Popover>
        )}
      </div>
      {/*
        Setting modal=true will disable interactions with outside elements and allow for the popover to be used
        within a Dialog component.
       */}
      <Popover modal={true} open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            className="w-full justify-start gap-2"
            disabled={loading || disabled}
            id={id}
            role="combobox"
            variant="outline">
            {teamsLoading ? null : selectedTeam ? (
              <>
                <span className="flex items-center gap-2">
                  <TeamSpot showAbbreviation={false} team={selectedTeam} />
                  {selectedTeam.teamName} - {selectedTeam.abbreviation}{' '}
                </span>
                <Badge variant="outline"> ID: {selectedTeam.id}</Badge>
              </>
            ) : (
              'Select a Team'
            )}
            {loading ? (
              <Spinner className="ml-auto size-4 shrink-0" />
            ) : (
              <ChevronDownIcon className="ml-auto size-4 shrink-0" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-full min-w-(--radix-popper-anchor-width) p-0">
          <Command
            filter={(value, search, keywords) => {
              const extendValue = value + ' ' + keywords?.join(' ');
              return new RegExp(search, 'gi').test(extendValue) ? 1 : 0;
            }}>
            <CommandInput placeholder="Search teams..." />
            <CommandList>
              <CommandEmpty>No teams found.</CommandEmpty>
              <CommandGroup>
                {data?.map((team) => {
                  const { abbreviation, id, name, teamName } = team;
                  return (
                    <CommandItem
                      key={id}
                      className="space-between flex w-full items-center gap-2"
                      keywords={[teamName, abbreviation, name]}
                      value={`${id}`}
                      onSelect={() => {
                        onSelectTeam(id);
                        setOpen(false);
                      }}>
                      <span className="flex flex-1 items-center gap-2">
                        <TeamSpot showAbbreviation={false} team={team} /> {teamName} -{' '}
                        {abbreviation} <Badge variant="outline"> ID: {id}</Badge>
                      </span>
                      {selectedTeamId === id && <Check>Selected</Check>}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
